import React, { useEffect, useState, useRef } from 'react';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/Button';
import { Monitor, Calendar, ArrowLeft, Mail, Phone, Send, CheckCircle } from 'lucide-react';
export function ExperiencePage() {
  const [isMobile, setIsMobile] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [contactMethod, setContactMethod] = useState<'email' | 'phone'>('email');
  const [contactValue, setContactValue] = useState('');
  const [isSent, setIsSent] = useState(false);
  // Check viewport width for mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  // Force-reset html/body styles and prevent scrolling
  useEffect(() => {
    if (!isMobile) {
      document.documentElement.style.cssText = 'margin:0;padding:0;overflow:hidden;height:100%';
      document.body.style.cssText = 'margin:0;padding:0;overflow:hidden;height:100%';
    }
    return () => {
      document.documentElement.style.cssText = '';
      document.body.style.cssText = '';
    };
  }, [isMobile]);
  const handleSendLink = (e: React.FormEvent) => {
    e.preventDefault();
    if (!contactValue.trim()) return;
    // In production, this would call an API to send the link
    setIsSent(true);
  };
  const handleIframeLoad = () => {
    setIsLoading(false);
  };
  // Mobile Fallback View
  if (isMobile) {
    return <div className="min-h-screen bg-cream flex flex-col" data-id="element-211">
        <Navbar minimal data-id="element-212" />
        <main className="flex-grow flex flex-col items-center justify-center px-6 py-10 text-center mt-[60px]" data-id="element-213">
          <div className="w-16 h-16 bg-forest/5 rounded-full flex items-center justify-center mb-5 text-forest" data-id="element-214">
            <Monitor size={36} strokeWidth={1.5} data-id="element-215" />
          </div>
          <h1 className="text-2xl font-bold text-forest mb-3" data-id="element-216">
            Best on Desktop
          </h1>
          <p className="text-charcoal/70 mb-8 max-w-sm text-base leading-relaxed" data-id="element-217">
            Our 3D home design experience is built for larger screens. Send
            yourself a link to pick up on your computer.
          </p>

          {/* Send Link Form */}
          {!isSent ? <div className="w-full max-w-sm" data-id="element-218">
              {/* Toggle — subtle text tabs, not buttons */}
              <div className="flex justify-center gap-6 mb-4" data-id="element-219">
                <button onClick={() => {
              setContactMethod('email');
              setContactValue('');
            }} className={`text-sm pb-1 transition-all border-b-2 ${contactMethod === 'email' ? 'text-forest border-forest font-medium' : 'text-charcoal/40 border-transparent'}`} data-id="element-220">
                  Email
                </button>
                <button onClick={() => {
              setContactMethod('phone');
              setContactValue('');
            }} className={`text-sm pb-1 transition-all border-b-2 ${contactMethod === 'phone' ? 'text-forest border-forest font-medium' : 'text-charcoal/40 border-transparent'}`} data-id="element-221">
                  Text message
                </button>
              </div>

              <form onSubmit={handleSendLink} className="flex gap-2" data-id="element-222">
                <input type={contactMethod === 'email' ? 'email' : 'tel'} placeholder={contactMethod === 'email' ? 'you@example.com' : '(555) 123-4567'} value={contactValue} onChange={e => setContactValue(e.target.value)} required className="flex-1 px-4 py-3 rounded-lg border border-forest/15 bg-white text-charcoal placeholder:text-charcoal/35 focus:outline-none focus:ring-2 focus:ring-forest/20 focus:border-forest/20 text-base" data-id="element-223" />
                <button type="submit" className="bg-gold hover:bg-[#b39249] text-forest px-4 py-3 rounded-lg transition-colors flex items-center justify-center" aria-label="Send link" data-id="element-224">
                  <Send size={18} data-id="element-225" />
                </button>
              </form>

              <p className="text-xs text-charcoal/35 mt-3" data-id="element-226">
                We'll send a direct link. No spam, ever.
              </p>
            </div> : <div className="w-full max-w-sm" data-id="element-227">
              <div className="bg-white rounded-xl p-6 border border-forest/10 shadow-sm" data-id="element-228">
                <div className="w-14 h-14 bg-forest/5 rounded-full flex items-center justify-center mx-auto mb-4 text-forest" data-id="element-229">
                  <CheckCircle size={28} strokeWidth={1.5} data-id="element-230" />
                </div>
                <h2 className="text-lg font-bold text-forest mb-2" data-id="element-231">
                  Link Sent!
                </h2>
                <p className="text-charcoal/70 text-sm" data-id="element-232">
                  Check your {contactMethod === 'email' ? 'inbox' : 'messages'}{' '}
                  for a link to design your dream home on desktop.
                </p>
              </div>
            </div>}

          {/* Secondary actions — text links only */}
          <div className="flex items-center gap-4 mt-8" data-id="element-233">
            <a href="#" className="text-sm text-charcoal/50 hover:text-forest transition-colors underline underline-offset-2" data-id="element-234">
              Book a tour
            </a>
            <span className="text-charcoal/20" data-id="element-235">·</span>
            <a href="/" className="text-sm text-charcoal/50 hover:text-forest transition-colors underline underline-offset-2" data-id="element-236">
              Back to home
            </a>
          </div>
        </main>
      </div>;
  }
  // Desktop: iframe is FULL VIEWPORT, navbar floats on top as overlay
  return <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    overflow: 'hidden',
    zIndex: 9999
  }} data-id="element-237">
      {/* Iframe — FULL VIEWPORT, no offset at all */}
      <iframe src="https://gdn.resvr.com/?cmsId=103291426" title="Crestwood Homes 3D Experience" scrolling="no" style={{
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      border: 'none',
      display: 'block',
      margin: 0,
      padding: 0,
      background: '#000',
      zIndex: 1
    }} allow="fullscreen; autoplay; clipboard-write; encrypted-media" onLoad={handleIframeLoad} data-id="element-238" />

      {/* Navbar — floating overlay ON TOP of iframe */}
      <div style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '50px',
      background: 'rgba(255,255,255,0.95)',
      backdropFilter: 'blur(8px)',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 24px',
      zIndex: 100
    }} data-id="element-239">
        <a href="/" style={{
        fontFamily: '"Playfair Display", serif',
        fontWeight: 700,
        color: '#1B3A2D',
        fontSize: '18px',
        letterSpacing: '0.05em',
        textDecoration: 'none'
      }} data-id="element-240">
          CRESTWOOD HOMES
        </a>
      </div>

      {/* Loading overlay */}
      {isLoading && <div style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#1B3A2D',
      zIndex: 10
    }} data-id="element-241">
          <div className="w-12 h-12 border-4 border-gold/30 border-t-gold rounded-full animate-spin mb-4" data-id="element-242"></div>
          <p className="text-cream font-medium tracking-wide" data-id="element-243">
            Loading Experience...
          </p>
        </div>}
    </div>;
}